/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

/*
 * contactMatch — pure helpers for the onboarding friend-discovery step.
 *
 * Normalizes raw device-contact rows to E.164 and resolves which ones are
 * already on MyAmbits via the existing /phoneIndex match engine
 * (batchLookupPhoneIndex — single-key point reads, viewer filtered out, no
 * enumeration). No raw number is persisted anywhere: normalization happens
 * in-memory and only the E.164 strings are read against /phoneIndex.
 *
 * Kept framework-agnostic (Database + a normalizer injected) so the matching
 * is unit-testable without a renderer or a Firebase emulator.
 */

import type { Database } from "firebase/database";
import { normalizeToE164 } from "@/utils/phone";
import {
  batchLookupPhoneIndex,
  type LookupOptions,
} from "@/utils/phoneIndexLookup";

export interface RawContact {
  /** Stable contact id from the device address book. */
  contactId: string;
  displayName: string;
  /** Best raw phone string off the contact (national or international). */
  rawPhone: string;
}

export interface NormalizedContact extends RawContact {
  /** E.164 form, or null when the raw number couldn't be parsed. */
  e164: string | null;
  initial: string;
}

export interface MatchedContact extends NormalizedContact {
  /** The MyAmbits uid this contact resolved to (already on the app). */
  matchedUid: string;
}

/**
 * Normalize a list of raw contacts to E.164. Drops nothing — unparsable
 * numbers keep `e164: null` so the caller can still show them (e.g. as
 * "invite" candidates) without surfacing them as matches.
 */
export function normalizeContacts(
  rows: readonly RawContact[],
  defaultRegion?: Parameters<typeof normalizeToE164>[1],
): NormalizedContact[] {
  return rows.map((r) => ({
    ...r,
    e164: normalizeToE164(r.rawPhone, defaultRegion),
    initial: r.displayName.trim()[0]?.toUpperCase() ?? "?",
  }));
}

/**
 * Resolve which normalized contacts are already on MyAmbits. Runs the E.164
 * set through the existing /phoneIndex engine and returns only the matches,
 * each carrying its resolved uid. The viewer's own uid is filtered by
 * batchLookupPhoneIndex, so a user never matches themselves.
 */
export async function matchContactsOnPhoneIndex(
  database: Database,
  contacts: readonly NormalizedContact[],
  viewerUid: string | null,
  options?: LookupOptions,
): Promise<MatchedContact[]> {
  const e164s = contacts
    .map((c) => c.e164)
    .filter((v): v is string => v !== null);
  if (e164s.length === 0) return [];

  const matchMap = await batchLookupPhoneIndex(database, e164s, viewerUid, options);
  if (matchMap.size === 0) return [];

  const matched: MatchedContact[] = [];
  for (const c of contacts) {
    if (!c.e164) continue;
    const uid = matchMap.get(c.e164);
    if (uid) matched.push({ ...c, matchedUid: uid });
  }
  return matched;
}
